import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Badge,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useTranslation } from "react-i18next"
import { showAlert } from "store/reducers/alertReducer"
import isPermissionEnabled from "common/isPermissionEnabled"
import TableShimmer from "components/Shared/TableShimmer"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DeleteModal from "components/Common/DeleteModal"
import {
  getAllTemplates,
  deleteTemplate,
  updateTemplate,
} from "store/actions/templatesAction"
import { getAllGiftCardCategories } from "store/actions/giftCardCategoriesAction"
import { cardCategories } from "components/Common/constants"
import formatDateTime from "components/Shared/formatDateTime"
import { filters } from "components/Common/constants"
import Switch from "components/Shared/Switch"

const Templates = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Gift Card Templates ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const { categories } = useSelector(state => state.GiftCardCategories)

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage = 1,
    itemsPerPage = 10,
    templates = [],
  } = useSelector(state => state.Templates)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  useEffect(() => {
    // Fetch categories when component mounts
    dispatch(
      getAllGiftCardCategories({
        perPage: 1000, // Get all categories
        pageNo: 1,
        active: null,
        orderBy: 1,
        keyword: "",
      })
    )
  }, [])

  // Custom filters without status
  const customFilters = [
    {
      name: "sortOrder",
      type: "select",
      get options() {
        return [
          { label: t("Ascending"), value: 1 },
          { label: t("Descending"), value: 2 },
        ]
      },
      get placeholder() {
        return t("Ascending")
      },
    },
    {
      name: "categoryId",
      type: "singleSelect",
      options: categories.map(item => ({
        label: item.cardType,
        value: item._id,
      })),
      placeholder: t("Select Category"),
    }
  ]

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      37,
      "gift_card_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      37,
      "gift_card_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      37,
      "gift_card_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      37,
      "gift_card_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    } else {
      fetchAllRecords()
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState({})
  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: 1,
    categoryId: "",

  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
    // Remove the immediate API call
  }

  const fetchAllRecords = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: selectedFilters.sortOrder,
      keyword: searchTerm || "",
      cardTypeId: selectedFilters?.categoryId?.value || "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllTemplates(payload))
    setIsApiCalled(true)
  }

  const getApiCall = (page = 1) => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: selectedFilters.sortOrder,
      keyword: searchTerm || "",
      cardTypeId: selectedFilters?.categoryId?.value || "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    return dispatch(getAllTemplates(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/templates?page=${page}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall()
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      dateRange: {},
    }
    dispatch(getAllTemplates(payload))
    navigate(`/templates?page=${1}`)
    setSelectedFilters({
      sortOrder: 1,
      categoryId: "",
    })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  const handleDelete = () => {
    setShowDeleteModal(false)
    const payload = {
      id: itemIdToDelete,
    }
    dispatch(deleteTemplate(payload)).then(() => {
      dispatch(
        showAlert({ text: "Template deleted successfully", type: "success" })
      )
      dispatch(
        getAllTemplates({
          perPage: itemsPerPage,
          pageNo: currentPage,
          orderBy: selectedFilters.sortOrder,
          keyword: searchTerm || "",
          dateRange:
            dateRange.min && dateRange.max
              ? { min: dateRange.min, max: dateRange.max }
              : {},
        })
      )
    })
  }

  

  // Initialize templates array if not present
  useEffect(() => {
    if (!templates) {
      dispatch(
        getAllTemplates({
          perPage: itemsPerPage,
          pageNo: 1,
        })
      )
    }
  }, [])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Gift Card Templates")}
                isAddBtn={loading ? false : usersPermissions.create}
                addBtnText={t("Add Template")}
                onAddBtnClick={() => navigate("/templates/add")}
              />
              <CardBody>
                <Filters
                  filters={customFilters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name")}
                  colSize={4}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                />
                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : !templates || templates.length === 0 ? (
                    <p className="text-center my-3">{t("No data found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th style={{ maxWidth: 50 }}>{t("S. No.")}</th>
                          <th>{t("Card Title")}</th>
                          <th>Category</th>
                          <th>{t("Share Title")}</th>
                          <th className="text-end">
                            <span
                              className="d-inline-block "
                              style={{
                                paddingRight: 30,
                                display: "block",
                              }}
                            >
                              {t("Action")}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {templates.map((item, index) => {
                          const serialNumber =
                            ((currentPage || 1) - 1) * (itemsPerPage || 10) +
                            index +
                            1
                          return (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(
                                  `/templates/details?id=${item?.cardTypeId?._id}`
                                )
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">{serialNumber}</th>
                              <td>
                                {language === "sa"
                                  ? item.card_title_on_app_ar
                                  : item.card_title_on_app_en || "-"}
                              </td>
                              <td>{item?.cardTypeId?.cardType}</td>
                              <td>
                                {language === "sa"
                                  ? item.share_gift_card_title_ar
                                  : item.share_gift_card_title_en || "-"}
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-end gap-2">
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="a"
                                      className="card-drop"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <i className="mdi mdi-dots-vertical font-size-18"></i>
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      {usersPermissions.edit && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            navigate(
                                              `/templates/edit?cardType=${item?.cardTypeId?._id}`
                                            )
                                          }}
                                        >
                                          <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                          {t("Edit")}
                                        </DropdownItem>
                                      )}
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/templates/details?id=${item?.cardTypeId?._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                        {t("View")}
                                      </DropdownItem>
                                      {usersPermissions.delete && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            setItemIdToDelete(item._id)
                                            setShowDeleteModal(true)
                                          }}
                                        >
                                          <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                          {t("Delete")}
                                        </DropdownItem>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && templates.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={templates}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setShowDeleteModal(false)
          setItemIdToDelete(null)
        }}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Template"),
        })}
      />
    </div>
  )
}

export default Templates
